/**
 * markdownTier2 — virtualClose
 *
 * Pure helper that closes UN-finished markdown fences before they're handed to
 * react-markdown, so the live stream can render incomplete code/KaTeX blocks
 * without violently re-rendering each delta as plain text + ``` and back.
 *
 * Scope is intentionally narrow:
 *   • triple-backtick code fences  (```)
 *   • triple-tilde code fences     (~~~)
 *   • KaTeX block delimiters       ($$)
 *   • ** bold spans                (see below)
 *
 * Inline markers (_italic_, single backtick, links) are LEFT UNTOUCHED —
 * react-markdown tolerates them and "fixing" them risks mis-rendering.
 *
 * ** bold is an exception: CommonMark renders an unclosed ** opener as
 * literal "**" text, which is visibly broken mid-stream (e.g. "**texto" shows
 * as "**texto" instead of rendering bold). An odd count of ** (excluding ***)
 * means the last opener has no closer — appending ** closes it.
 */

/**
 * @param {string} text
 * @returns {string}
 */
export function virtualClose(text) {
  if (typeof text !== 'string' || text.length === 0) return text || '';

  let out = text;

  // Triple-backtick code fences.
  const tripleBacktickCount = (out.match(/```/g) || []).length;
  if (tripleBacktickCount % 2 === 1) {
    out += '\n```';
  }

  // Triple-tilde code fences.
  const tripleTildeCount = (out.match(/~~~/g) || []).length;
  if (tripleTildeCount % 2 === 1) {
    out += '\n~~~';
  }

  // KaTeX block delimiters ($$).
  const dollarBlockCount = (out.match(/\$\$/g) || []).length;
  if (dollarBlockCount % 2 === 1) {
    out += '\n$$';
  }

  // Unclosed ** bold markers. \*\*(?!\*) matches ** not followed by a third *,
  // so *** (bold+italic) is excluded from the count (best-effort).
  const boldCount = (out.match(/\*\*(?!\*)/g) || []).length;
  if (boldCount % 2 === 1) {
    out += '**';
  }

  return out;
}
